import firebase from "firebase/app";

export const messageCollectionKey = "messages";

export type MessageType =
  | "text"
  | "image"
  | "video"
  | "audio"
  | "file"
  | "location"
  | "sticker"
  | "button_template"
  | "basic_button_template"
  | "confirm_template"
  | "submission_teacher_comment"
  | "multiple";

export type MessageUserType = "student" | "tutor" | "admin" | "system" | "bot";

export type Message = {
  ref: firebase.firestore.DocumentReference;
  data: MessageData;
};

export type MessageData = {
  from: MessageUser;
  type: MessageType;
  messageText?: string;
  messageContents?: MessageContent[];
  template?: ButtonTemplateMessage; // [deprecated]
  basicButtonTemplate?: BasicButtonTemplateMessage;
  confirmTemplate?: ConfirmTemplateMessage;
  submissionTeacherComment?: SubmissionTeacherCommentMessage;
  /** 先生が生徒の AI 会話の発言を引用して送った文（type: "text"）に付く。画面は引用のカードで出す */
  quote?: MessageQuote;
  /** 引用した文・赤ペンを入れた画像の元になった会話。画面はその会話ログを開く導線を出す */
  sourceConversation?: MessageSourceConversation;
  lineId: string;
  timestamp: number;
  transfer?: boolean;
  checked?: boolean;
  deleted?: boolean;
};

export type MessageUser = {
  type: MessageUserType;
  userId: string;
  name: string;
};

/**
 * 引用して送ったメッセージの引用の中身。messageText には引用を含めた素の文も入れてあるので、
 * LINE や古いクライアントはそちらをそのまま出せる
 */
export type MessageQuote = {
  /** 引用した発言の持ち主。user は生徒の発話、assistant は AI の返答 */
  source: "user" | "assistant";
  /** 引用した発言の本文（Markdown のまま） */
  text: string;
  /** 先生が添えた文。無ければ空文字 */
  message: string;
};

/**
 * 先生が生徒の AI 会話から引用した文・赤ペンを入れた画像の、元になった会話。
 * 画面はその会話ログを開く導線を出す（先生は会話ログ閲覧、生徒は自分の会話）
 */
export type MessageSourceConversation = {
  /** 会話 doc のパス（schools/{s}/classrooms/{c}/students/{uid}/schoolAIConversations/{id}） */
  conversationPath: string;
  /** 会話のアプリ（prompt doc）の ID。生徒が自分の会話を開くときに使う */
  promptId?: string;
  /** 会話のアプリ名（表示用） */
  appTitle?: string;
};

export type SubmissionTeacherCommentMessage = {
  commentRef: firebase.firestore.DocumentReference;
  conversationRef: firebase.firestore.DocumentReference;
  authorUid: string;
  authorName: string;
  appTitle: string;
  submissionBoxTitle: string;
  submissionTitle: string;
  submittedAt: number;
  updatedAt: number;
};

export type MessageContent = {
  type: MessageType;
  text: string;
  buttonTemplate?: BasicButtonTemplateMessage;
  confirmTemplate?: ConfirmTemplateMessage;
};

export type ButtonTemplateMessage = {
  altText: string;
  buttonLabel: string;
  buttonUri: string;
  title?: string;
  thumbnailUrl?: string;
};

export type BasicButtonTemplateMessage = {
  altText: string;
  title?: string;
  thumbnailUrl?: string;
  actions: TemplateMessageAction[]; // max-length: 4
};

export type ConfirmTemplateMessage = {
  altText: string;
  text: string;
  actions: [TemplateMessageAction, TemplateMessageAction];
};

export type TemplateMessageAction<
  ExtraFields = {
    label: string;
  }
> = (
  | PostbackAction
  | MessageAction
  | URIAction
  | {
      type: "camera";
    }
  | {
      type: "cameraRoll";
    }
  | {
      type: "location";
    }
) &
  ExtraFields;

export type PostbackAction = {
  type: "postback";
  data: string;
  displayText?: string;
};

export type MessageAction = {
  type: "message";
  text: string;
};

export type URIAction = {
  type: "uri";
  uri: string;
};

export type PostbackActionType = "end_learning" | "rest_learning";

export const actionEndLearning: PostbackActionType = "end_learning";
export const actionRestLearning: PostbackActionType = "rest_learning";
export const availableActionTypes: string[] = [
  actionEndLearning,
  actionRestLearning
];

export const messageTextsToAvoidNotification = ["> 休憩", "> 終了"];

// student の [schoolDocId, classroomDocId, studentDocId, messageDocId] を返す
export function getDocIdsOfMessageRef(
  ref: firebase.firestore.DocumentReference
): [string, string, string, string] {
  if (!ref.parent.parent) {
    return ["", "", "", ""];
  }
  const studentRef = ref.parent.parent;
  if (!studentRef.parent.parent) {
    return ["", "", "", ""];
  }
  const classroomRef = studentRef.parent.parent;
  if (!classroomRef.parent.parent) {
    return ["", "", "", ""];
  }
  const schoolRef = classroomRef.parent.parent;
  return [schoolRef.id, classroomRef.id, studentRef.id, ref.id];
}

export function convertToMessage(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): Message {
  const returnData: MessageData = {
    from: data.from,
    type: data.type,
    messageText: data.messageText,
    template: data.template,
    lineId: data.lineId,
    timestamp: data.timestamp,
    transfer: data.transfer,
    checked: data.checked ?? true, //checkedが存在しない → 既読とみなす
    deleted: data.deleted ?? false
  };

  if (data.template) {
    returnData.template = data.template;
  }
  if (data.basicButtonTemplate) {
    returnData.basicButtonTemplate = data.basicButtonTemplate;
  }
  if (data.confirmTemplate) {
    returnData.confirmTemplate = data.confirmTemplate;
  }
  if (data.submissionTeacherComment) {
    returnData.submissionTeacherComment = data.submissionTeacherComment;
  }
  if (data.quote) {
    returnData.quote = data.quote;
  }
  if (data.sourceConversation) {
    returnData.sourceConversation = data.sourceConversation;
  }

  return {
    ref,
    data: returnData
  };
}
